import { stripLeadingTrailingQuotes } from "@storytell/sutils";

// EnvironmentConfig is a type that represents configuration values
// available on the server which are encoded in the HTML response
// to be made available to the client.
export type EnvironmentConfig = {
  pullRequestId: string;
  apiTarget: string;
  gitHash: string;
  firebaseAPIKey: string;
  firebaseAuthDomain: string;
  firebaseProjectId: string;
  firebaseStorageBucket: string;
  firebaseMessagingSenderId: string;
};

// cache is a global singleton variable that stores the environment
// configuration values. It is used to avoid multiple calls to
// get individual environment variables or deserialize them on the client.
//
// All values are multi-thread safe as they do not change for the lifetime
// of the application.
let cache: EnvironmentConfig | null = null;

export function getEnvironmentConfig(): EnvironmentConfig {
  if (cache && Object.keys(cache).length > 0) {
    return cache;
  }
  cache = {
    pullRequestId: getPullRequestId$(),
    apiTarget: getApiTarget$(),
    gitHash: getGitHash$(),
    firebaseAPIKey: getFirebaseAPIKey$(),
    firebaseAuthDomain: getFirebaseAuthDomain$(),
    firebaseProjectId: getFirebaseProjectId$(),
    firebaseStorageBucket: getFirebaseStorageBucket$(),
    firebaseMessagingSenderId: getFirebaseMessagingSenderId$(),
  };
  return cache;
}

/**
 * Gets the Vercel pull request ID from environment variables
 * @returns The pull request ID or empty string if not available
 */
export const getPullRequestId$ = () => {
  return typeof VERCEL_GIT_PULL_REQUEST_ID === "string" ? stripLeadingTrailingQuotes(VERCEL_GIT_PULL_REQUEST_ID) : "";
};

/**
 * Gets the Storytell API target environment from environment variables
 * @returns The API target environment with quotes stripped
 */
export const getApiTarget$ = () => {
  return stripLeadingTrailingQuotes(STORYTELL_API_TARGET);
};

/**
 * Gets the Git commit hash from environment variables
 * @returns The Git commit hash or "unknown" if not available
 */
export const getGitHash$ = () => {
  return typeof VERCEL_GIT_COMMIT_SHA === "string" ? stripLeadingTrailingQuotes(VERCEL_GIT_COMMIT_SHA) : "unknown";
};

/**
 * Gets the Firebase API key from environment variables
 * @returns The Firebase API key with quotes stripped
 */
export const getFirebaseAPIKey$ = () => {
  return stripLeadingTrailingQuotes(FIREBASE_API_KEY);
};

/**
 * Gets the Firebase authentication domain from environment variables
 * @returns The Firebase auth domain with quotes stripped
 */
export const getFirebaseAuthDomain$ = () => {
  return stripLeadingTrailingQuotes(FIREBASE_AUTH_DOMAIN);
};

/**
 * Gets the Firebase project ID from environment variables
 * @returns The Firebase project ID with quotes stripped
 */
export const getFirebaseProjectId$ = () => {
  return stripLeadingTrailingQuotes(FIREBASE_PROJECT_ID);
};

/**
 * Gets the Firebase storage bucket from environment variables
 * @returns The Firebase storage bucket with quotes stripped
 */
export const getFirebaseStorageBucket$ = () => {
  return stripLeadingTrailingQuotes(FIREBASE_STORAGE_BUCKET);
};

/**
 * Gets the Firebase messaging sender ID from environment variables
 * @returns The Firebase messaging sender ID with quotes stripped
 */
export const getFirebaseMessagingSenderId$ = () => {
  return stripLeadingTrailingQuotes(FIREBASE_MESSAGING_SENDER_ID);
};

/**
 * Gets the Firebase app ID from environment variables
 * @returns The Firebase app ID with quotes stripped
 */
export const getFirebaseAppId$ = () => {
  return stripLeadingTrailingQuotes(FIREBASE_APP_ID);
};

/**
 * Gets the Vercel environment from environment variables
 * @returns The Vercel environment with quotes stripped
 */
export const getVercelEnv$ = () => {
  return typeof VERCEL_ENV === "string" ? stripLeadingTrailingQuotes(VERCEL_ENV) : "";
};

/**
 * Gets the Vercel URL from environment variables
 * @returns The Vercel URL with quotes stripped
 */
export const getVercelURL$ = () => {
  return typeof VERCEL_URL === "string" ? stripLeadingTrailingQuotes(VERCEL_URL) : "";
};

/**
 * Gets the Vercel branch URL from environment variables
 * @returns The Vercel branch URL with quotes stripped
 */
export const getVercelBranchURL$ = () => {
  return typeof VERCEL_BRANCH_URL === "string" ? stripLeadingTrailingQuotes(VERCEL_BRANCH_URL) : "";
};

/**
 * Gets the Git commit reference from environment variables
 * @returns The Git commit reference with quotes stripped
 */
export const getGitCommitRef$ = () => {
  return typeof VERCEL_GIT_COMMIT_REF === "string" ? stripLeadingTrailingQuotes(VERCEL_GIT_COMMIT_REF) : "";
};

/**
 * Gets the Vercel commit SHA from environment variables
 * @returns The Vercel commit SHA with quotes stripped
 */
export const getVercelCommitSHA$ = () => {
  return typeof VERCEL_GIT_COMMIT_SHA === "string" ? stripLeadingTrailingQuotes(VERCEL_GIT_COMMIT_SHA) : "";
};
